import { FC, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { DataTable } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { useFormationSlotsQuery } from 'pages/type-curves/hooks';
import type { FormationSlot } from 'types/api-responses';
import {
  buildUnitBuilderSlotColumns,
  isGeneratedSlot,
  intersectsUnit,
  type UnitXExtent,
} from './unit-builder-slots-columns';
import { useUnitBuilder } from './context';
import { scopedSlotKeysAtom } from './atoms';
import style from './unit-builder-modal.module.scss';

interface UnitBuilderSlotsTableProps {
  typeCurveHeaderDetailsId: number;
  collectionIds?: number[];
  /** The scoped unit's section-line extent, for the Intersects column. */
  unitExtent?: UnitXExtent | null;
}

/**
 * The per-lane slot id a row is scoped by — the same `{unit}_{formation}_{n}`
 * the gun barrel keys on. A composite leg's `slotId` is the composite's name
 * (shared by every leg), so its lane id is rebuilt from its parts.
 *
 * @param {FormationSlot} slot - The slot row.
 * @return {string | null} The lane id, or null for a row with none.
 */
export const laneSlotId = (slot: FormationSlot): string | null => {
  const isLeg =
    slot.compositeId !== null &&
    slot.compositeId !== undefined &&
    slot.slotNumber !== null &&
    slot.slotNumber !== undefined;
  if (isLeg) {
    return `${slot.developmentAreaName}_${slot.formation}_${slot.slotNumber}`;
  }
  return slot.slotId;
};

/**
 * The builder's slots table.
 *
 * Same query and column defs as the shared `SlotTable`, re-ordered to the mock's
 * layout and with its three extra columns (Name, Assigned to Unit, Intersects
 * the Unit). Rows are selectable because the selection is shared: ticking a row
 * scopes that slot for the map's trim grips and the gun barrel's nudge.
 *
 * @param {UnitBuilderSlotsTableProps} props - Query inputs and the unit extent.
 * @return {JSX.Element} The table.
 */
export const UnitBuilderSlotsTable: FC<UnitBuilderSlotsTableProps> = ({
  typeCurveHeaderDetailsId,
  collectionIds,
  unitExtent = null,
}) => {
  const { unitName } = useUnitBuilder();
  const [scopedSlotKeys, setScopedSlotKeys] = useAtom(scopedSlotKeysAtom);
  const { slots, isLoading, error } = useFormationSlotsQuery(
    typeCurveHeaderDetailsId,
    collectionIds
  );

  const columns = useMemo(
    () => buildUnitBuilderSlotColumns(unitName, unitExtent),
    [unitName, unitExtent]
  );

  // Show every LTD for every formation the cross-section crosses — no client-side
  // scoping of the ROWS. Selection matches what the gun barrel lets you pick: a
  // GENERATED LTD slot (existing wells with a UWI aren't) that reaches into the
  // unit width (`intersectsUnit` — the same extent the chart uses), in ANY
  // formation, so a merge can span formations from either side. Other-formation
  // picks can't be moved; edits still act on the scoped band. `_canSelect` is the
  // DataTable's per-row selection gate.
  const rows = useMemo(
    () =>
      slots.map((s) => ({
        ...s,
        _canSelect:
          unitExtent !== null &&
          isGeneratedSlot(s) &&
          intersectsUnit(s, unitExtent) === true,
      })),
    [slots, unitExtent]
  );

  // Same keying as the shared table: slots carry no id of their own, and a
  // WeakMap lets entries go with the rows they describe.
  const rowIdMap = useMemo(() => {
    const map = new WeakMap<FormationSlot, string>();
    const used = new Set<string>();

    for (const row of rows) {
      // Joined on `|`, which cannot occur in these values — `-` can, and does:
      // both `developmentAreaName` ("U-0000062323") and `slotId`
      // ("U-0000062323_MSS_2") carry dashes, so a `-` join let `a-b` + `c` and
      // `a` + `b-c` compose the same key from different rows.
      //
      // `xOffsetFt` / `tvdFt` are what actually separate sibling slots of one
      // formation: for an LTD slot `uwi` is "--" and the area and formation are
      // shared, so without geometry the key rests on `slotId` alone and collapses
      // to the literal "no-slot" when that is null.
      //
      // `slotNumber` is what separates the LANE slots of one crossing well: both
      // rows carry its bare UWI as `slotId` and the same unit and formation, and
      // a U-turn can be cut at one x in both lanes, so without the lane the key
      // rests on a positional suffix rather than on the row's own identity.
      const base = [
        row.developmentAreaName,
        row.formation ?? 'no-formation',
        row.uwi || 'no-uwi',
        row.slotId ?? 'no-slot',
        row.slotNumber ?? 'no-slot-number',
        row.xOffsetFt ?? 'no-x',
        row.tvdFt ?? 'no-tvd',
      ].join('|');

      // Uniqueness is a property of this loop, not of the data: ask the set
      // rather than trust a counter. The previous counter recorded against the
      // suffixed key instead of the original, so from the third identical row on
      // every row was handed the same `-1` id — duplicate React keys, which is
      // what made rows appear twice or bind to the wrong slot once the search box
      // re-filtered the list.
      let id = base;
      let n = 1;
      while (used.has(id)) {
        id = `${base}#${n}`;
        n += 1;
      }
      used.add(id);
      map.set(row, id);
    }

    return map;
  }, [rows]);

  const rowIdFn = useCallback(
    (row: FormationSlot) => rowIdMap.get(row) ?? 'unknown',
    [rowIdMap]
  );

  // Row id → slot, so a ticked row can be turned into the chart's scope key.
  const slotByRowId = useMemo(() => {
    const map = new Map<string, FormationSlot>();
    for (const row of rows) {
      const id = rowIdMap.get(row);
      if (id) map.set(id, row);
    }
    return map;
  }, [rows, rowIdMap]);

  /**
   * Ticking a row scopes that slot, which is what lets it be moved — the mock:
   * "click a slot inside the unit (or tick its row in the table) to scope it —
   * only then can it be moved or trimmed".
   *
   * Keys match the chart's own format: `slot:{slotId}` for generated slots,
   * `uwi:{uwi}` for existing wells.
   */
  const handleSelectionChange = useCallback(
    (state: TableState) => {
      const keys = new Set<string>();
      for (const rowId of state.selection.toggledNodes) {
        const slot = slotByRowId.get(rowId);
        if (!slot) continue;
        const lane = laneSlotId(slot);
        if (lane) keys.add(`slot:${lane}`);
        else if (slot.uwi) keys.add(`uwi:${slot.uwi}`);
      }
      setScopedSlotKeys(keys);
    },
    [slotByRowId, setScopedSlotKeys]
  );

  // Rows the chart considers scoped, so a chart click ticks the row too.
  const selectedRowIds = useMemo(() => {
    const ids: string[] = [];
    for (const [rowId, slot] of slotByRowId) {
      const lane = laneSlotId(slot);
      let key: string | null = null;
      if (lane) key = `slot:${lane}`;
      else if (slot.uwi) key = `uwi:${slot.uwi}`;
      if (key && scopedSlotKeys.has(key)) ids.push(rowId);
    }
    return ids;
  }, [slotByRowId, scopedSlotKeys]);

  return (
    <div className={style.slotsTable}>
      <DataTable<FormationSlot>
        data={rows}
        columns={columns}
        getRowId={rowIdFn}
        isLoading={isLoading}
        error={error}
        // Its own key: the column set and order differ from the shared table, and
        // DataTable persists visibility per key.
        persistenceKey="unit-builder-slot-table-v1"
        scroll={{ mode: 'paginated' }}
        // Controlled: the scope is shared with the chart, so the table reflects
        // it rather than owning it.
        controlledState={{
          selection: { selectAll: false, toggledNodes: selectedRowIds },
        }}
        onSelectionChange={handleSelectionChange}
        config={{
          selectionType: 'multi',
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: true,
        }}
        searchPlaceholder="Search slots..."
      />
    </div>
  );
};
